import React, { PureComponent } from "react"
import classNames from "classnames"
import { Mutation } from "react-apollo"

import {
  UPSERT_FOUNDER_ACTION,
  DELETE_FOUNDER_ACTION,
  MY_FOUNDER_ASSIST_QUERY,
} from "../../lib/founder-assist-queries"

import "./founder-action-card.scss"

const PRIORITIES = [
  { key: "high", label: "High" },
  { key: "medium", label: "Medium" },
  { key: "low", label: "Low" },
  { key: "unprioritized", label: "Clear" },
]

const DAY_MS = 24 * 60 * 60 * 1000

function relativeDate(iso) {
  if (!iso) return ""
  const days = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / DAY_MS))
  if (days === 0) return "today"
  if (days === 1) return "yesterday"
  if (days < 14) return `${days} days ago`
  if (days < 56) return `${Math.round(days / 7)} weeks ago`
  return `${Math.round(days / 30)} months ago`
}

function FlagIcon({ priority }) {
  return (
    <svg
      width="14"
      height="16"
      viewBox="0 0 14 16"
      fill={priority === "unprioritized" ? "none" : "currentColor"}
      stroke="currentColor"
      strokeWidth={priority === "unprioritized" ? 1.5 : 1.2}
      strokeLinejoin="round"
      strokeLinecap="round"
      aria-hidden
    >
      <path d="M2 1.5v13" />
      <path d="M2 2.5h9.4a0.4 0.4 0 0 1 0.32 0.64L10 5l1.72 1.86A0.4 0.4 0 0 1 11.4 7.5H2" />
    </svg>
  )
}

class FounderActionCard extends PureComponent {
  constructor(props) {
    super(props)
    this.state = {
      title: props.action.title || "",
      notes: props.action.notes || "",
      editingTitle: false,
      editingNotes: false,
      flagOpen: false,
      menuOpen: false,
      impactOpen: false,
      confirmDelete: false,
    }
    this._titleRef = React.createRef()
    this._notesRef = React.createRef()
    this._flagRef = React.createRef()
    this._menuRef = React.createRef()
    this._impactRef = React.createRef()
  }

  componentDidMount() {
    this._mounted = true
    document.addEventListener("mousedown", this.handleClickOutside)
  }

  componentWillUnmount() {
    this._mounted = false
    document.removeEventListener("mousedown", this.handleClickOutside)
  }

  safeSetState(state) {
    if (this._mounted) this.setState(state)
  }

  componentDidUpdate(prevProps) {
    if (prevProps.action.title !== this.props.action.title)
      this.setState({ title: this.props.action.title || "" })
    if (prevProps.action.notes !== this.props.action.notes)
      this.setState({ notes: this.props.action.notes || "" })
  }

  handleClickOutside = (e) => {
    if (
      this.state.flagOpen &&
      this._flagRef.current &&
      !this._flagRef.current.contains(e.target)
    ) {
      this.setState({ flagOpen: false })
    }
    if (
      this.state.menuOpen &&
      this._menuRef.current &&
      !this._menuRef.current.contains(e.target)
    ) {
      this.setState({ menuOpen: false })
    }
    if (
      this.state.impactOpen &&
      this._impactRef.current &&
      !this._impactRef.current.contains(e.target)
    ) {
      this.setState({ impactOpen: false })
    }
  }

  startEditTitle = () => {
    this.setState({ editingTitle: true }, () => {
      if (this._titleRef.current) this._titleRef.current.focus()
    })
  }

  finishEditTitle = (save) => {
    const trimmed = this.state.title.trim()
    if (!trimmed) {
      this.setState({
        title: this.props.action.title || "",
        editingTitle: false,
      })
      return
    }
    if (trimmed === (this.props.action.title || "")) {
      this.setState({ editingTitle: false })
      return
    }
    save({
      variables: { id: this.props.action.id, title: trimmed },
    }).then(() => this.safeSetState({ editingTitle: false }))
  }

  cancelEditTitle = () => {
    this.setState({
      title: this.props.action.title || "",
      editingTitle: false,
    })
  }

  startEditNotes = () => {
    this.setState({ editingNotes: true, menuOpen: false }, () => {
      if (this._notesRef.current) this._notesRef.current.focus()
    })
  }

  finishEditNotes = (save) => {
    if (this.state.notes === (this.props.action.notes || "")) {
      this.setState({ editingNotes: false })
      return
    }
    save({
      variables: {
        id: this.props.action.id,
        notes: this.state.notes,
      },
    }).then(() => this.safeSetState({ editingNotes: false }))
  }

  setPriority = (priority, save) => {
    save({
      variables: { id: this.props.action.id, priority },
    })
    this.setState({ flagOpen: false })
  }

  archiveAction = (save) => {
    save({
      variables: { id: this.props.action.id, status: "archived" },
    }).then(() => {
      if (this.props.onArchived) this.props.onArchived(this.props.action)
    })
  }

  reopenAction = (save) => {
    save({
      variables: { id: this.props.action.id, status: "open" },
    })
  }

  renderImpact(score) {
    const { action } = this.props
    const { impactOpen } = this.state
    const n = Number.isFinite(score) ? score : 0
    const rationale = action.actionRationale || ""
    const hasRationale = Boolean(rationale && rationale.trim())

    const dots = [1, 2, 3, 4, 5].map((i) => (
      <span
        key={i}
        className={classNames("founder-action-card__impact-dot", {
          "founder-action-card__impact-dot--on": i <= n,
        })}
      />
    ))

    if (!hasRationale) {
      return (
        <span
          className="founder-action-card__impact"
          title={n > 0 ? `Impact ${n}/5` : "No impact score"}
          aria-label={n > 0 ? `Impact ${n} of 5` : "No impact score"}
        >
          {dots}
        </span>
      )
    }

    return (
      <span
        className="founder-action-card__impact-wrap"
        ref={this._impactRef}
      >
        <button
          type="button"
          className={classNames(
            "founder-action-card__impact",
            "founder-action-card__impact--interactive",
            { "founder-action-card__impact--open": impactOpen }
          )}
          title={`Impact ${n}/5 — ${rationale}`}
          aria-label={`Impact ${n} of 5. Click for reasoning.`}
          aria-expanded={impactOpen}
          onClick={() =>
            this.setState((p) => ({ impactOpen: !p.impactOpen }))
          }
        >
          {dots}
        </button>
        {impactOpen && (
          <div
            className="founder-action-card__rationale"
            role="tooltip"
          >
            <span className="founder-action-card__rationale-eyebrow">
              Why this scored {n}/5
            </span>
            <p>{rationale}</p>
          </div>
        )}
      </span>
    )
  }

  renderSourceLine() {
    const { action } = this.props
    const when = relativeDate(action.createdAt)
    if (!when) return null
    const author = action.source === "user" ? "Added by you" : "Suggested"
    return (
      <span className="founder-action-card__source">
        {author} {when}
      </span>
    )
  }

  render() {
    const { action } = this.props
    const {
      title,
      notes,
      editingTitle,
      editingNotes,
      flagOpen,
      menuOpen,
      confirmDelete,
    } = this.state
    const priority = action.priority || "unprioritized"
    const isArchived = action.status === "archived"

    return (
      <Mutation
        mutation={UPSERT_FOUNDER_ACTION}
        refetchQueries={[{ query: MY_FOUNDER_ASSIST_QUERY }]}
      >
        {(save) => (
          <div
            className={classNames(
              "founder-action-card",
              `founder-action-card--${priority}`,
              { "founder-action-card--archived": isArchived }
            )}
            tabIndex={0}
            role="listitem"
          >
            <div className="founder-action-card__head">
              <div
                className="founder-action-card__flag-wrap"
                ref={this._flagRef}
              >
                <button
                  type="button"
                  className={classNames(
                    "founder-action-card__flag",
                    `founder-action-card__flag--${priority}`
                  )}
                  onClick={() =>
                    this.setState((p) => ({ flagOpen: !p.flagOpen }))
                  }
                  aria-haspopup="menu"
                  aria-expanded={flagOpen}
                  aria-label={`Priority: ${priority}. Click to change.`}
                  title={
                    priority === "unprioritized"
                      ? "Set priority"
                      : `Priority: ${priority}`
                  }
                >
                  <FlagIcon priority={priority} />
                </button>
                {flagOpen && (
                  <div
                    className="founder-action-card__flag-menu"
                    role="menu"
                  >
                    {PRIORITIES.map((p) => (
                      <button
                        key={p.key}
                        role="menuitem"
                        type="button"
                        className={classNames(
                          "founder-action-card__flag-menu-item",
                          `founder-action-card__flag-menu-item--${p.key}`,
                          {
                            "founder-action-card__flag-menu-item--current":
                              p.key === priority,
                          }
                        )}
                        onClick={() => this.setPriority(p.key, save)}
                      >
                        <FlagIcon priority={p.key} />
                        <span>{p.label}</span>
                        {p.key === priority && (
                          <span
                            className="founder-action-card__flag-menu-check"
                            aria-hidden
                          >
                            ✓
                          </span>
                        )}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {editingTitle ? (
                <input
                  ref={this._titleRef}
                  className="founder-action-card__title-input"
                  value={title}
                  onChange={(e) => this.setState({ title: e.target.value })}
                  onBlur={() => this.finishEditTitle(save)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") this.finishEditTitle(save)
                    if (e.key === "Escape") this.cancelEditTitle()
                  }}
                />
              ) : (
                <h4
                  className="founder-action-card__title"
                  onClick={this.startEditTitle}
                  title="Click to edit"
                >
                  {action.title}
                </h4>
              )}

              {this.renderImpact(action.impactScore)}

              <div
                className="founder-action-card__menu-wrap"
                ref={this._menuRef}
              >
                <button
                  type="button"
                  className="founder-action-card__menu-trigger"
                  aria-haspopup="menu"
                  aria-expanded={menuOpen}
                  onClick={() =>
                    this.setState((p) => ({ menuOpen: !p.menuOpen }))
                  }
                  title="More"
                >
                  <span aria-hidden>⋯</span>
                </button>
                {menuOpen && (
                  <div className="founder-action-card__menu" role="menu">
                    {!notes && !isArchived && (
                      <button
                        role="menuitem"
                        type="button"
                        onClick={this.startEditNotes}
                      >
                        Add a note
                      </button>
                    )}
                    {notes && !isArchived && (
                      <button
                        role="menuitem"
                        type="button"
                        onClick={this.startEditNotes}
                      >
                        Edit note
                      </button>
                    )}
                    {isArchived && (
                      <button
                        role="menuitem"
                        type="button"
                        onClick={() => {
                          this.setState({ menuOpen: false })
                          this.reopenAction(save)
                        }}
                      >
                        Reopen
                      </button>
                    )}
                    <button
                      role="menuitem"
                      type="button"
                      className="founder-action-card__menu-destructive"
                      onClick={() =>
                        this.setState({
                          menuOpen: false,
                          confirmDelete: true,
                        })
                      }
                    >
                      Delete
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* notes body */}
            {editingNotes ? (
              <div className="founder-action-card__notes-edit">
                <textarea
                  ref={this._notesRef}
                  className="founder-action-card__notes-input"
                  value={notes}
                  onChange={(e) => this.setState({ notes: e.target.value })}
                  onBlur={() => this.finishEditNotes(save)}
                  placeholder="The why, or your first step…"
                  rows={2}
                />
              </div>
            ) : (
              notes && (
                <p
                  className="founder-action-card__notes"
                  onClick={this.startEditNotes}
                  title="Click to edit note"
                >
                  {notes}
                </p>
              )
            )}

            {/* footer */}
            <div className="founder-action-card__foot">
              {this.renderSourceLine()}
              {!isArchived && (
                <button
                  type="button"
                  className="founder-action-card__complete"
                  onClick={() => this.archiveAction(save)}
                  aria-label="Mark action complete"
                >
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.4"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden
                  >
                    <path d="M5 12.5l4.5 4.5L19 7.5" />
                  </svg>
                  Complete
                </button>
              )}
            </div>

            {confirmDelete && (
              <Mutation
                mutation={DELETE_FOUNDER_ACTION}
                refetchQueries={[{ query: MY_FOUNDER_ASSIST_QUERY }]}
              >
                {(del, { loading: deleting }) => (
                  <div className="founder-action-card__confirm" role="dialog">
                    <p>Delete this action permanently?</p>
                    <div className="founder-action-card__confirm-buttons">
                      <button
                        className="founder-action-card__btn-destructive"
                        disabled={deleting}
                        onClick={() =>
                          del({ variables: { id: action.id } }).then(() =>
                            this.setState({ confirmDelete: false })
                          )
                        }
                      >
                        Delete
                      </button>
                      <button
                        className="founder-action-card__btn-ghost"
                        disabled={deleting}
                        onClick={() =>
                          this.setState({ confirmDelete: false })
                        }
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
              </Mutation>
            )}
          </div>
        )}
      </Mutation>
    )
  }
}

export default FounderActionCard
